/**
 * 可缩放图片组件 — 滚轮缩放 + 拖拽平移 + 双击切换 + 工具栏
 * 纯 CSS transform 实现，零外部依赖
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface Props {
  src: string;
  alt?: string;
  className?: string;
}

const MAX_SCALE = 5;
const ZOOM_STEP = 0.15;
const DOUBLE_CLICK_SCALE = 2;

const ZoomableImage: React.FC<Props> = ({ src, alt, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const [showToolbar, setShowToolbar] = useState(false);
  const toolbarTimer = useRef<ReturnType<typeof setTimeout>>();
  const naturalSize = useRef({ w: 0, h: 0 });
  const displaySize = useRef({ w: 0, h: 0 });
  const minScale = useRef(1);

  const isZoomed = scale > 1.01;

  const updateDisplaySize = useCallback(() => {
    const el = containerRef.current;
    const nw = naturalSize.current.w;
    const nh = naturalSize.current.h;
    if (!el || nw === 0 || nh === 0) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const ir = nw / nh;
    const cr = cw / ch;
    if (ir > cr) { displaySize.current = { w: cw, h: cw / ir }; }
    else          { displaySize.current = { w: ch * ir, h: ch }; }
    minScale.current = Math.min(cw / nw, ch / nh, 1);
  }, []);

  const clampTranslate = useCallback((s: number, tx: number, ty: number) => {
    if (s <= 1) return { x: 0, y: 0 };
    const dw = displaySize.current.w;
    const dh = displaySize.current.h;
    return {
      x: Math.min(Math.max(-(dw * s - dw) / 2, tx), (dw * s - dw) / 2),
      y: Math.min(Math.max(-(dh * s - dh) / 2, ty), (dh * s - dh) / 2),
    };
  }, []);

  const zoomAt = useCallback((newScale: number, cx: number, cy: number) => {
    const clamped = Math.min(MAX_SCALE, Math.max(minScale.current, newScale));
    const ratio = clamped / scale;
    const el = containerRef.current;
    if (!el) return;
    const pw = el.clientWidth / 2;
    const ph = el.clientHeight / 2;
    const px = cx - pw;
    const py = cy - ph;
    const newTx = px - ratio * (px - translate.x);
    const newTy = py - ratio * (py - translate.y);
    setScale(clamped);
    setTranslate(clampTranslate(clamped, newTx, newTy));
  }, [scale, translate, clampTranslate]);

  const onImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    naturalSize.current = { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight };
    updateDisplaySize();
  }, [updateDisplaySize]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      updateDisplaySize();
      setTranslate(prev => clampTranslate(scale, prev.x, prev.y));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale, clampTranslate, updateDisplaySize]);

  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    naturalSize.current = { w: 0, h: 0 };
  }, [src]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) zoomAt(scale + dir * ZOOM_STEP, e.clientX - rect.left, e.clientY - rect.top);
    setShowToolbar(true);
    if (toolbarTimer.current) clearTimeout(toolbarTimer.current);
    toolbarTimer.current = setTimeout(() => setShowToolbar(false), 2000);
  }, [scale, zoomAt]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isZoomed) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, tx: translate.x, ty: translate.y };
  }, [isZoomed, translate]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setTranslate(clampTranslate(scale, dragStart.current.tx + dx, dragStart.current.ty + dy));
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, scale, clampTranslate]);

  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (isZoomed) {
      setScale(1); setTranslate({ x: 0, y: 0 }); setShowToolbar(false);
    } else {
      zoomAt(DOUBLE_CLICK_SCALE, e.clientX - rect.left, e.clientY - rect.top);
      setShowToolbar(true);
      if (toolbarTimer.current) clearTimeout(toolbarTimer.current);
      toolbarTimer.current = setTimeout(() => setShowToolbar(false), 2000);
    }
  }, [isZoomed, zoomAt]);

  const reset = useCallback(() => {
    setScale(1); setTranslate({ x: 0, y: 0 }); setShowToolbar(false);
  }, []);

  useEffect(() => { return () => { if (toolbarTimer.current) clearTimeout(toolbarTimer.current); }; }, []);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden flex items-center justify-center ${className}`}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      style={{ cursor: isZoomed ? (dragging ? 'grabbing' : 'grab') : 'default' }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        onLoad={onImgLoad}
        className="max-w-full max-h-full object-contain select-none"
        style={{
          transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
          transformOrigin: 'center center',
          transition: dragging ? 'none' : 'transform 0.2s ease-out',
        }}
      />
      {showToolbar && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
          <span>{Math.round(scale * 100)}%</span>
          {isZoomed && (
            <button onClick={(e) => { e.stopPropagation(); reset(); }} className="px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
              <ArrowPathIcon className="w-5 h-5 inline-block" /> 复位
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ZoomableImage;
